import { useState, useRef, useMemo, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { KeyboardEvent } from "react";
import {
  Box,
  Flex,
  Text,
  Card,
  Button,
  TextArea,
  IconButton,
  Badge,
  Skeleton,
  Avatar,
  TextField,
  Select,
  Callout,
  Tabs,
  Heading,
  Switch,
  Dialog,
  Slider,
} from "@radix-ui/themes";
import { useParams, useNavigate } from "@tanstack/react-router";
import {
  Send,
  Trash2,
  Upload,
  Users,
  ExternalLink,
  AlertTriangle,
  Info,
  Copy,
  RefreshCw,
  Check,
} from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  useBoard,
  useMessages,
  useCreateMessage,
  useDeleteMessage,
  usePhotos,
  useUploadPhoto,
  useDeletePhoto,
  usePromotePhoto,
  useMembers,
  useAddMember,
  useRemoveMember,
  useUpdateBoard,
  useDeleteBoard,
  useBoardHistory,
} from "@/hooks/use-api";
import type { Message, Photo, Member, BoardHistoryItem } from "@/lib/api";
import { resolveAssetUrl, getToken, generateDisplayToken } from "@/lib/api";
import { pb } from "@/lib/pb";
import { buildAppDisplayPath, buildDedicatedDisplayUrl } from "@/lib/display-url";
import { useOverflowDebug } from "@/hooks/use-overflow-debug";
import { SUPPORTED_LANGUAGES, type LanguageCode } from "@/lib/i18n";
import {
  DEFAULT_IMAGE_DURATION_MS,
  DEFAULT_MESSAGE_DURATION_MS,
  DEFAULT_WEATHER_DURATION_MS,
  MAX_SLIDE_DURATION_MS,
  MIN_SLIDE_DURATION_MS,
} from "@mem/shared";
import { PhotoCard } from "@/components/boards/photo-card";
import { WeatherTab } from "@/components/boards/weather-tab";
import { ProgrammeTab } from "@/components/boards/programme-tab";
import { useVirtualizer } from "@tanstack/react-virtual";

// ─── Messages Tab ──────────────────────────────────────────────

function MessagesTab({ slug }: { slug: string }) {
  const { data: messages, isLoading } = useMessages(slug);
  const createMessage = useCreateMessage(slug);
  const deleteMessage = useDeleteMessage(slug);
  const [text, setText] = useState("");
  const { t } = useTranslation();

  async function handleSend() {
    const trimmed = text.trim();
    if (!trimmed) return;
    await createMessage.mutateAsync({ text: trimmed });
    setText("");
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <Flex direction="column" gap="4">
      {/* Composer */}
      <Card>
        <Flex gap="3" align={{ initial: "stretch", sm: "end" }} direction={{ initial: "column", sm: "row" }}>
          <TextArea
            placeholder={t("board.message_placeholder")}
            style={{ flexGrow: 1 }}
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Button
            onClick={handleSend}
            disabled={!text.trim() || createMessage.isPending}
            style={{ minHeight: 40 }}
          >
            <Send size={16} />
            {t("board.message_send")}
          </Button>
        </Flex>
      </Card>

      {/* Liste */}
      <Flex direction="column" gap="2">
        {isLoading &&
          [...Array(3)].map((_, i) => <Skeleton key={i} height="60px" />)}

        {!isLoading && messages?.length === 0 && (
          <Box py="6" style={{ textAlign: "center" }}>
            <Text color="gray" size="2" as="p">
              {t("board.messages_empty")}
            </Text>
          </Box>
        )}

        {messages?.map((msg: Message) => (
          <Card key={msg.id}>
            <Flex justify="between" align="start" gap="3">
              <Flex direction="column" gap="1" style={{ flexGrow: 1, minWidth: 0 }}>
                <Text size="2" style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                  {msg.text}
                </Text>
                <Text size="1" color="gray">
                  {msg.author_name} · {msg.created ? new Date(msg.created).toLocaleDateString("fr-FR") : "—"}
                </Text>
              </Flex>
              <IconButton
                variant="ghost"
                color="red"
                size="1"
                onClick={() => deleteMessage.mutate(msg.id)}
                aria-label={t("common.delete")}
              >
                <Trash2 size={14} />
              </IconButton>
            </Flex>
          </Card>
        ))}
      </Flex>
    </Flex>
  );
}

// ─── Photos Tab ────────────────────────────────────────────────

const PHOTO_CARD_MIN_WIDTH = 160;
const PHOTO_CARD_EST_HEIGHT = 200;
const PHOTO_GAP = 12; // var(--space-3)

function PhotosTab({ slug }: { slug: string }) {
  const query = usePhotos(slug);
  const uploadPhoto = useUploadPhoto(slug);
  const deletePhoto = useDeletePhoto(slug);
  const promotePhoto = usePromotePhoto(slug);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Aplatir les pages
  const allPhotos = useMemo(
    () => query.data?.pages.flatMap((p) => p.photos) ?? [],
    [query.data],
  );

  // Mesurer la largeur du conteneur → nombre de colonnes
  const [columns, setColumns] = useState(4);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentBoxSize[0]?.inlineSize ?? entry.contentRect.width;
      setColumns(Math.max(1, Math.floor((w + PHOTO_GAP) / (PHOTO_CARD_MIN_WIDTH + PHOTO_GAP))));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Nombre de lignes pour la virtualisation
  const rows = useMemo(
    () => Math.ceil(allPhotos.length / columns),
    [allPhotos.length, columns],
  );

  // Virtualiseur par lignes
  const rowVirtualizer = useVirtualizer({
    count: rows,
    getScrollElement: () => containerRef.current,
    estimateSize: () => PHOTO_CARD_EST_HEIGHT,
    overscan: 3,
  });

  // Sentinel pour infinite scroll
  const sentinelRef = useRef<HTMLDivElement>(null);
  const hasNextPage = query.hasNextPage === true;
  const isFetchingNextPage = query.isFetchingNextPage;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          query.fetchNextPage();
        }
      },
      { root: containerRef.current, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, query.fetchNextPage]);

  function handleFiles(files: FileList | null) {
    if (!files) return;
    Array.from(files).forEach((file) => {
      uploadPhoto.mutate({ file });
    });
  }

  const isLoading = query.isLoading;
  const isEmpty = !isLoading && allPhotos.length === 0;

  return (
    <Flex direction="column" gap="4" style={{ height: "100%", minHeight: 0 }}>
      {/* Upload zone */}
      <Card
        style={{
          border: "2px dashed var(--gray-5)",
          cursor: "pointer",
          transition: "border-color 0.15s",
          flexShrink: 0,
        }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer.files);
        }}
      >
        <Flex direction="column" align="center" gap="2" py="6">
          <Upload size={24} color="var(--gray-8)" />
          <Text size="2" color="gray">
            {t("board.photos_upload_hint")}{" "}
            <Text size="2" color="violet" style={{ cursor: "pointer" }}>
              {t("board.photos_browse")}
            </Text>
          </Text>
          <Text size="1" color="gray">
            {t("board.photos_formats")}
          </Text>
        </Flex>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </Card>

      {/* État : chargement */}
      {isLoading && (
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(${PHOTO_CARD_MIN_WIDTH}px, 1fr))`,
            gap: "var(--space-3)",
          }}
        >
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} height="160px" style={{ borderRadius: "var(--radius-3)" }} />
          ))}
        </Box>
      )}

      {/* État : vide */}
      {isEmpty && (
        <Box py="4" style={{ textAlign: "center" }}>
          <Text color="gray" size="2" as="p">
            {t("board.photos_empty")}
          </Text>
        </Box>
      )}

      {/* Grille virtualisée */}
      {!isEmpty && (
        <Box
          ref={containerRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Box
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const rowStart = virtualRow.index * columns;
              const rowPhotos = allPhotos.slice(rowStart, rowStart + columns);

              return (
                <Box
                  key={virtualRow.key}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                    display: "grid",
                    gridTemplateColumns: `repeat(${columns}, 1fr)`,
                    gap: "var(--space-3)",
                    height: PHOTO_CARD_EST_HEIGHT,
                  }}
                >
                  {rowPhotos.map((photo: Photo) => (
                    <PhotoCard
                      key={photo.id}
                      photo={photo}
                      onDelete={(id) => deletePhoto.mutate(id)}
                      onPromote={(id) => promotePhoto.mutate(id)}
                      isDeleting={deletePhoto.isPending && deletePhoto.variables === photo.id}
                      isPromoting={promotePhoto.isPending && promotePhoto.variables === photo.id}
                    />
                  ))}
                </Box>
              );
            })}
          </Box>

          {/* Sentinel */}
          <Box ref={sentinelRef} style={{ height: 1 }} />

          {/* Indicateur de chargement de page suivante */}
          {isFetchingNextPage && (
            <Flex justify="center" py="3">
              <Skeleton width="120px" height="16px" />
            </Flex>
          )}
        </Box>
      )}
    </Flex>
  );
}

// ─── History Tab ───────────────────────────────────────────────

/** Dot coloré sur la timeline */
function TimelineDot({ color }: { color: string }) {
  return (
    <Box
      data-testid="timeline-dot"
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: color,
        flexShrink: 0,
        marginTop: 4,
      }}
    />
  );
}

/** Vignette 56×56 cliquable avec lightbox Radix Dialog */
function PhotoThumb({
  src,
  alt,
  token,
}: {
  src: string;
  alt: string;
  token: string | null;
}) {
  const url = resolveAssetUrl(src, token);
  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <Box
          data-testid="photo-thumb"
          style={{
            width: 56,
            height: 56,
            borderRadius: "var(--radius-2)",
            overflow: "hidden",
            flexShrink: 0,
            cursor: "pointer",
          }}
        >
          <img
            src={url}
            alt={alt}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        </Box>
      </Dialog.Trigger>
      <Dialog.Content
        style={{ maxWidth: "90vw", maxHeight: "90vh", padding: 0, overflow: "hidden" }}
      >
        <img
          src={url}
          alt={alt}
          style={{ width: "100%", height: "auto", display: "block", maxHeight: "90vh", objectFit: "contain" }}
        />
      </Dialog.Content>
    </Dialog.Root>
  );
}

function HistoryTab({ slug }: { slug: string }) {
  const { data: items, isLoading } = useBoardHistory(slug);
  const token = getToken();
  const { t } = useTranslation();

  return (
    <Box style={{ position: "relative" }}>
      {/* Ligne verticale de la timeline */}
      {!isLoading && items && items.length > 0 && (
        <Box
          data-testid="timeline-line"
          style={{
            position: "absolute",
            left: 4,
            top: 8,
            bottom: 8,
            width: 2,
            backgroundColor: "var(--gray-4)",
          }}
        />
      )}

      <Flex direction="column" gap="3">
        {isLoading && [...Array(4)].map((_, i) => <Skeleton key={i} height="52px" />)}

        {!isLoading && items?.length === 0 && (
          <Box py="6" style={{ textAlign: "center" }}>
            <Text color="gray" size="2" as="p">
              {t("board.history_empty")}
            </Text>
          </Box>
        )}

        {items?.map((item: BoardHistoryItem) => {
          const when = new Date(item.created).toLocaleString("fr-FR", {
            dateStyle: "short",
            timeStyle: "short",
          });

          if (item.kind === "message") {
            return (
              <Flex key={`message-${item.id}`} align="start" gap="3" style={{ paddingLeft: 0 }}>
                <TimelineDot color="var(--blue-9)" />
                <Flex direction="column" gap="1" style={{ flex: 1, minWidth: 0 }}>
                  <Flex align="center" gap="2" wrap="wrap">
                    <Badge variant="soft" color="blue" size="1">
                      {t("board.tab_messages")}
                    </Badge>
                    <Text size="1" color="gray">
                      {item.author_name} · {when}
                    </Text>
                  </Flex>
                  <Text
                    size="2"
                    style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                  >
                    {item.message.text}
                  </Text>
                </Flex>
              </Flex>
            );
          }

          return (
            <Flex key={`photo-${item.id}`} align="start" gap="3">
              <TimelineDot color="var(--violet-9)" />
              <Flex direction="column" gap="1" style={{ flex: 1, minWidth: 0 }}>
                <Flex align="center" gap="2" wrap="wrap">
                  <Badge variant="soft" color="violet" size="1">
                    {t("board.tab_photos")}
                  </Badge>
                  <Text size="1" color="gray">
                    {item.author_name} · {when}
                  </Text>
                </Flex>
                <Flex align="center" gap="2">
                  <PhotoThumb
                    src={item.photo.image_url}
                    alt={item.photo.caption || "Photo"}
                    token={token}
                  />
                  {item.photo.caption ? (
                    <Text size="2" color="gray" style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                      {item.photo.caption}
                    </Text>
                  ) : null}
                </Flex>
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
}

// ─── Members Tab ───────────────────────────────────────────────

function MembersTab({ slug }: { slug: string }) {
  const { data: members, isLoading } = useMembers(slug);
  const addMember = useAddMember(slug);
  const removeMember = useRemoveMember(slug);
  const { register, handleSubmit, reset } = useForm<{ email: string; role: string }>({
    defaultValues: { email: "", role: "contributor" },
  });
  const { t } = useTranslation();

  const [role, setRole] = useState("contributor");

  async function onAdd(data: { email: string; role: string }) {
    await addMember.mutateAsync({ email: data.email, role });
    reset();
  }

  const roleLabel: Record<string, string> = {
    admin: t("board.role_admin"),
    contributor: t("board.role_contributor"),
    recipient: t("board.role_recipient"),
  };

  const roleBadgeColor: Record<string, "violet" | "blue" | "green"> = {
    admin: "violet",
    contributor: "blue",
    recipient: "green",
  };

  return (
    <Flex direction="column" gap="4">
      {/* Ajouter un membre */}
      <Card>
        <Text size="2" weight="medium" mb="3" as="p">
          {t("board.add_member")}
        </Text>
        <form onSubmit={handleSubmit(onAdd)}>
          <Flex gap="2" align={{ initial: "stretch", sm: "end" }} direction={{ initial: "column", sm: "row" }}>
            <Box style={{ flexGrow: 1, minWidth: 0 }}>
              <TextField.Root
                placeholder="email@exemple.com"
                type="email"
                size="2"
                {...register("email", { required: true })}
              />
            </Box>
            <Select.Root value={role} onValueChange={setRole} size="2">
              <Select.Trigger />
              <Select.Content>
                <Select.Item value="admin">{t("board.role_admin")}</Select.Item>
                <Select.Item value="contributor">{t("board.role_contributor")}</Select.Item>
                <Select.Item value="recipient">{t("board.role_recipient")}</Select.Item>
              </Select.Content>
            </Select.Root>
            <Button type="submit" size="2" disabled={addMember.isPending}>
              <Users size={14} />
              {t("board.add_member_button")}
            </Button>
          </Flex>
        </form>
      </Card>

      {/* Liste membres */}
      <Flex direction="column" gap="2">
        {isLoading && [...Array(3)].map((_, i) => <Skeleton key={i} height="52px" />)}

        {members?.map((member: Member) => (
          <Card key={member.id}>
            <Flex
              justify="between"
              align={{ initial: "start", sm: "center" }}
              direction={{ initial: "column", sm: "row" }}
              gap="2"
              style={{ minWidth: 0 }}
            >
                <Flex align="center" gap="3" style={{ minWidth: 0, flexGrow: 1 }}>
                  <Avatar
                    fallback={member.name?.[0]?.toUpperCase() ?? "?"}
                    size="2"
                    radius="full"
                  />
                  <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
                    <Text size="2" weight="medium" truncate>
                      {member.name || member.email}
                    </Text>
                    <Text size="1" color="gray" truncate>
                      {member.email}
                    </Text>
                  </Flex>
                </Flex>
              <Flex align="center" gap="2">
                <Badge
                  color={roleBadgeColor[member.role] ?? "gray"}
                  variant="soft"
                  size="1"
                >
                  {roleLabel[member.role] ?? member.role}
                </Badge>
                <IconButton
                  variant="ghost"
                  color="red"
                  size="1"
                  onClick={() => removeMember.mutate(member.id)}
                  aria-label={t("common.delete")}
                >
                  <Trash2 size={14} />
                </IconButton>
              </Flex>
            </Flex>
          </Card>
        ))}
      </Flex>
    </Flex>
  );
}

// ─── Settings Tab ──────────────────────────────────────────────

const MIN_DURATION_SEC = Math.round(MIN_SLIDE_DURATION_MS / 1000);
const MAX_DURATION_SEC = Math.round(MAX_SLIDE_DURATION_MS / 1000);

function clampDurationSec(value: number): number {
  if (!Number.isFinite(value)) return MIN_DURATION_SEC;
  if (value < MIN_DURATION_SEC) return MIN_DURATION_SEC;
  if (value > MAX_DURATION_SEC) return MAX_DURATION_SEC;
  return Math.round(value);
}

interface DurationSliderFieldProps {
  label: string;
  helpText: string;
  unitSingular: string;
  unitPlural: string;
  value: number;
  onChange: (value: number) => void;
}

function DurationSliderField({
  label,
  helpText,
  unitSingular,
  unitPlural,
  value,
  onChange,
}: DurationSliderFieldProps) {
  const safeValue = clampDurationSec(value);
  const unit = safeValue <= 1 ? unitSingular : unitPlural;
  return (
    <Flex direction="column" gap="2">
      <Flex align="baseline" justify="between" gap="3">
        <Text as="label" size="2" weight="medium">
          {label}
        </Text>
        <Text size="2" weight="bold" color="gray">
          {safeValue} {unit}
        </Text>
      </Flex>
      <Slider
        value={[safeValue]}
        min={MIN_DURATION_SEC}
        max={MAX_DURATION_SEC}
        step={1}
        onValueChange={(next) => {
          const v = next[0];
          if (typeof v === "number") onChange(clampDurationSec(v));
        }}
      />
      <Text size="1" color="gray">
        {helpText}
      </Text>
    </Flex>
  );
}

function SettingsTab({ slug }: { slug: string }) {
  const { data: board } = useBoard(slug);
  const updateBoard = useUpdateBoard(slug);
  const deleteBoard = useDeleteBoard();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);

  const displayToken = board?.settings?.display_token;
  const pocketBaseToken = pb.authStore.token || undefined;
  const displayUrl = displayToken
    ? buildDedicatedDisplayUrl(slug, displayToken)
    : null;
  const dedicatedDisplayPreviewUrl = buildDedicatedDisplayUrl(
    slug,
    board?.public ? undefined : pocketBaseToken,
  );
  const canOpenDedicatedDisplay = Boolean(board?.public || pocketBaseToken);

  const genToken = useMutation({
    mutationFn: () => generateDisplayToken(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["board", slug] });
    },
  });

  function handleCopy() {
    if (!displayUrl) return;
    navigator.clipboard.writeText(displayUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const { register, handleSubmit, control } = useForm({
    values: {
      name: board?.name ?? "",
      description: board?.description ?? "",
      public: board?.public ?? false,
      kioskLanguage: (board?.settings?.language ?? "fr") as LanguageCode,
      messageDurationSec: Math.round(
        (board?.settings?.messageDurationMs ?? DEFAULT_MESSAGE_DURATION_MS) / 1000,
      ),
      imageDurationSec: Math.round(
        (board?.settings?.imageDurationMs ?? DEFAULT_IMAGE_DURATION_MS) / 1000,
      ),
      weatherDurationSec: Math.round(
        (board?.settings?.weatherDurationMs ?? DEFAULT_WEATHER_DURATION_MS) / 1000,
      ),
    },
  });

  async function onSave(data: {
    name: string;
    description: string;
    public: boolean;
    kioskLanguage: LanguageCode;
    messageDurationSec: number;
    imageDurationSec: number;
    weatherDurationSec: number;
  }) {
    await updateBoard.mutateAsync({
      name: data.name,
      description: data.description,
      public: data.public,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      settings: {
        ...(board?.settings ?? {}),
        language: data.kioskLanguage,
        messageDurationMs: data.messageDurationSec * 1000,
        imageDurationMs: data.imageDurationSec * 1000,
        weatherDurationMs: data.weatherDurationSec * 1000,
      } as any,
    });
  }

  function handleDelete() {
    if (confirm(t("board.delete_confirm_text", { name: board?.name }))) {
      deleteBoard.mutate(slug, {
        onSuccess: () => navigate({ to: "/dashboard" }),
      });
    }
  }

  return (
    <Flex direction="column" gap="5">
      {/* Général */}
      <Card>
        <Text size="3" weight="bold" mb="4" as="p">
          {t("board.general_settings")}
        </Text>
        <form onSubmit={handleSubmit(onSave)}>
          <Flex direction="column" gap="4">
            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium">
                {t("board.name_label")}
              </Text>
              <TextField.Root size="3" {...register("name")} />
            </Flex>
            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium">
                {t("board.description_label")}
              </Text>
              <TextArea rows={3} {...register("description")} />
            </Flex>
            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium">
                {t("board.language_label")}
              </Text>
              <Controller
                name="kioskLanguage"
                control={control}
                render={({ field }) => (
                  <Select.Root value={field.value} onValueChange={field.onChange} size="2">
                    <Select.Trigger />
                    <Select.Content>
                      {SUPPORTED_LANGUAGES.map((l) => (
                        <Select.Item key={l.code} value={l.code}>
                          {l.flag} {l.label}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                )}
              />
              <Text size="1" color="gray">
                {t("board.kiosk_language_help")}
              </Text>
            </Flex>
            <Controller
              name="imageDurationSec"
              control={control}
              render={({ field }) => (
                <DurationSliderField
                  label={t("board.image_duration_label")}
                  helpText={t("board.image_duration_help")}
                  unitSingular={t("board.duration_unit_singular")}
                  unitPlural={t("board.duration_unit_plural")}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="messageDurationSec"
              control={control}
              render={({ field }) => (
                <DurationSliderField
                  label={t("board.message_duration_label")}
                  helpText={t("board.message_duration_help")}
                  unitSingular={t("board.duration_unit_singular")}
                  unitPlural={t("board.duration_unit_plural")}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="weatherDurationSec"
              control={control}
              render={({ field }) => (
                <DurationSliderField
                  label={t("board.weather_duration_label")}
                  helpText={t("board.weather_duration_help")}
                  unitSingular={t("board.duration_unit_singular")}
                  unitPlural={t("board.duration_unit_plural")}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Flex
              align={{ initial: "start", sm: "center" }}
              justify="between"
              direction={{ initial: "column", sm: "row" }}
              gap="2"
            >
              <Box style={{ minWidth: 0 }}>
                <Text size="2" weight="medium" as="p">
                  {t("board.public_label")}
                </Text>
                <Text size="1" color="gray" as="p">
                  {t("board.public_description")}
                </Text>
              </Box>
              <Controller
                name="public"
                control={control}
                render={({ field }) => (
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
            </Flex>
            <Flex justify="end">
              <Button type="submit" disabled={updateBoard.isPending}>
                {updateBoard.isPending ? t("board.save_pending") : t("board.save_button")}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Card>

      {/* Lien display kiosque */}
      <Card>
        <Text size="3" weight="bold" mb="3" as="p">
          {t("board.display_view_title")}
        </Text>
        <Text size="2" color="gray" as="p" mb="4">
          {t("board.display_view_help")}
        </Text>

        {displayUrl ? (
          <Flex direction="column" gap="3">
            <Flex gap="2" align="center">
              <TextField.Root
                value={displayUrl}
                readOnly
                size="2"
                style={{ flex: 1, fontFamily: "monospace", fontSize: 11 }}
              />
              <IconButton
                size="2"
                variant="soft"
                onClick={handleCopy}
                title={t("board.copy_url")}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </IconButton>
            </Flex>
            <Flex gap="2" align="center">
              <Button
                size="2"
                variant="soft"
                onClick={() => window.open(displayUrl, "_blank")}
              >
                <ExternalLink size={14} />
                {t("board.open")}
              </Button>
              <Button
                size="2"
                variant="soft"
                color="gray"
                onClick={() => window.open(dedicatedDisplayPreviewUrl, "_blank", "noopener,noreferrer")}
                disabled={!canOpenDedicatedDisplay}
              >
                <ExternalLink size={14} />
                {t("board.display_mode_btn")}
              </Button>
              <Button
                size="2"
                variant="ghost"
                color="gray"
                onClick={() => {
                  if (confirm(t("board.regenerate_token_confirm"))) {
                    genToken.mutate();
                  }
                }}
                disabled={genToken.isPending}
              >
                <RefreshCw size={14} />
                {t("board.regenerate_token")}
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Flex direction="column" gap="3" align="start">
            <Text size="2" color="gray" as="p">
              {t("board.no_display_token")}
            </Text>
            <Flex gap="2" align="center" wrap="wrap">
              <Button
                size="2"
                variant="soft"
                color="gray"
                onClick={() => window.open(dedicatedDisplayPreviewUrl, "_blank", "noopener,noreferrer")}
                disabled={!canOpenDedicatedDisplay}
              >
                <ExternalLink size={14} />
                {t("board.display_mode_btn")}
              </Button>
              <Button
                size="2"
                onClick={() => genToken.mutate()}
                disabled={genToken.isPending}
              >
                <RefreshCw size={14} />
                {t("board.generate_token")}
              </Button>
            </Flex>
          </Flex>
        )}
      </Card>

      {/* Zone danger */}
      <Card>
        <Callout.Root color="red" variant="soft" mb="4">
          <Callout.Icon>
            <AlertTriangle size={16} />
          </Callout.Icon>
          <Callout.Text>
            {t("board.delete_zone_warning")}
          </Callout.Text>
        </Callout.Root>
        <Button color="red" variant="soft" onClick={handleDelete} disabled={deleteBoard.isPending}>
          <Trash2 size={14} />
          {t("board.delete_button")}
        </Button>
      </Card>
    </Flex>
  );
}

// ─── Board Page ────────────────────────────────────────────────

export function BoardPage() {
  const { slug } = useParams({ from: "/_auth/boards/$slug" });
  const navigate = useNavigate();
  useOverflowDebug(`board-admin:${slug}`);
  const { data: board, isLoading, isError } = useBoard(slug);
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Box>
        <Skeleton height="32px" width="40%" mb="4" />
        <Skeleton height="16px" width="60%" mb="6" />
        <Skeleton height="400px" />
      </Box>
    );
  }

  if (isError || !board) {
    return (
      <Callout.Root color="red">
        <Callout.Icon>
          <Info size={16} />
        </Callout.Icon>
        <Callout.Text>{t("board.not_found")}</Callout.Text>
      </Callout.Root>
    );
  }

  return (
    <Box style={{ minWidth: 0, overflowX: "clip" }}>
      {/* Header */}
      <Flex justify="between" align={{ initial: "start", sm: "center" }} mb="4" gap="3" wrap="wrap">
        <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
          <Heading size="5">{board.name}</Heading>
          <Text size="2" color="gray" truncate>
            {board.description || `mem.pm/${board.slug}`}
          </Text>
        </Flex>
        <Button
          variant="soft"
          size="2"
          onClick={() => window.open(buildAppDisplayPath(slug), "_blank", "noopener,noreferrer")}
          style={{ minHeight: 40 }}
        >
          <ExternalLink size={14} />
          {t("board.display_view_btn")}
        </Button>
      </Flex>

      {/* Onglets */}
      <Tabs.Root defaultValue="programme">
        <Box
          mb="4"
          style={{
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
          className="tabs-scroll-x"
        >
          <Tabs.List style={{ whiteSpace: "nowrap", width: "max-content", minWidth: "100%" }}>
            <Tabs.Trigger value="programme">{t("board.tab_programme")}</Tabs.Trigger>
            <Tabs.Trigger value="messages">{t("board.tab_messages")}</Tabs.Trigger>
            <Tabs.Trigger value="photos">{t("board.tab_photos")}</Tabs.Trigger>
            <Tabs.Trigger value="weather">{t("board.tab_weather")}</Tabs.Trigger>
            <Tabs.Trigger value="history">{t("board.tab_history")}</Tabs.Trigger>
            <Tabs.Trigger value="members">{t("board.tab_members")}</Tabs.Trigger>
            <Tabs.Trigger value="settings">{t("board.tab_settings")}</Tabs.Trigger>
          </Tabs.List>
        </Box>

        <Tabs.Content value="programme">
          <ProgrammeTab slug={slug} />
        </Tabs.Content>
        <Tabs.Content value="messages">
          <MessagesTab slug={slug} />
        </Tabs.Content>
        <Tabs.Content value="history">
          <HistoryTab slug={slug} />
        </Tabs.Content>
        <Tabs.Content value="photos">
          <PhotosTab slug={slug} />
        </Tabs.Content>
        <Tabs.Content value="weather">
          <WeatherTab slug={slug} />
        </Tabs.Content>
        <Tabs.Content value="members">
          <MembersTab slug={slug} />
        </Tabs.Content>
        <Tabs.Content value="settings">
          <SettingsTab slug={slug} />
        </Tabs.Content>
      </Tabs.Root>
    </Box>
  );
}
